import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BookingActivity } from 'src/app/models/booking-activity/booking-activity';
import { BookingMember } from 'src/app/models/booking-member/booking-member';
import { BookingMemberDetailExtend } from 'src/app/models/booking-member/booking-member-detail-extend';
import { ScheduleInfoBasicDto } from 'src/app/models/calendar/schedule/schedule-info-basic';
import { MemberInfoQr } from 'src/app/models/qr/member-info-qr';
import { HttpService } from 'src/app/services/http/http.service';
import { PermissionService } from 'src/app/services/user/permission.service';
import { UserService } from 'src/app/services/user/user.service';
import { ScheduleCancel } from '../models/schedule-cancel';

@Injectable({
    providedIn: 'root'
})
export class UpdateService extends HttpService {
    private readonly permissions = {
        canEditActivitiesCalendar: this.permissionService.canEditActivitiesCalendar(),
        canValidateSchedule: this.permissionService.canValidateSchedule(),
        canValidateOnlyMyActivities: this.permissionService.canValidateOnlyMyActivities(),
        canValidateOnlyTodayActivities: this.permissionService.canValidateOnlyTodayActivities(),
    };

    constructor(private userService: UserService, private permissionService: PermissionService) {
        super();
    }

    cancelSchedule(scheduleState: ScheduleCancel): Observable<any> {
        const url = `schedules/${scheduleState.id}/cancel`;

        return this.post(url, { app: 'booking', version: 2.2 }, scheduleState, this.showSuccessMessage('correct_activity_cancel'));
    }

    validateSchedule(scheduleState: ScheduleCancel): Observable<any> {
        const url = `schedules/${scheduleState.id}/validate`;

        return this.post(url, { app: 'booking', version: 2.2 }, scheduleState, this.showSuccessMessage('correct_activity_validate'));
    }

    deleteSchedules(schedules: number[]): Observable<void> {
        const url = 'schedules';
        const options = { ...this.showSuccessMessage('correct_activity_remove'), body: schedules };

        return this.delete(url, { app: 'booking', version: 2 }, options);
    }

    canValidateScheduledActivity(activity: ScheduleInfoBasicDto, dateProgram: Date): boolean {
        const canValidateOnlyMyActivities = this.canValidateOnlyMyActivities(activity);
        const canValidateOnlyTodayActivities = this.canValidateOnlyTodayActivities(dateProgram);

        return this.canValidateFull()
            || (canValidateOnlyMyActivities && !this.permissions.canValidateOnlyTodayActivities)
            || (canValidateOnlyTodayActivities && !this.permissions.canValidateOnlyMyActivities)
            || (canValidateOnlyMyActivities && canValidateOnlyTodayActivities);
    }

    canOpenScheduledActivity(activity: ScheduleInfoBasicDto, dateProgram: Date): boolean {
        return this.permissions.canEditActivitiesCalendar || this.canValidateScheduledActivity(activity, dateProgram);
    }

    disabledUpdateStaff(): boolean {
        return  this.permissions.canValidateOnlyMyActivities;
    }

    checkCanManageActivitiesCalendar(): boolean {
        return this.permissions.canEditActivitiesCalendar;
    }

    listMembersBooking(idSchedule: number): Observable<BookingMemberDetailExtend> {
        const url = `bookings/schedules/${idSchedule}/extend`;

        return this.get(url, { app: 'booking', version: 2 });
    }

    validateManualAccessBookingMemberList(idSchedule: number, listBookedMembers: number[]): Observable<BookingMember[]> {
        const url = `bookings/schedules/${idSchedule}/list/validate`;

        return this.post(url, { app: 'booking', version: 2 }, listBookedMembers, this.showSuccessMessage('attendance_validate_successfully', false));
    }

    cancelManualBookingMemberList(idSchedule: number, listBookedMembers: number[]): Observable<BookingMember[]> {
        const url = `bookings/schedules/${idSchedule}/members`;
        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json', showSuccessMessage: 'canceled_booking' }),
            body: listBookedMembers
        };

        return this.delete(url, { app: 'booking', version: 2 }, options);
    }

    setMemberBooking(
        selectedMemberId: string,
        scheduleId: number,
        bookedSeat: number | null,
        checkPaymentRestriction: boolean,
        idMemberFee: string | null,
        forceBuyWithCredits: boolean | null): Observable<any> {
        const bookingActivity: BookingActivity = {
            idMember: 0,
            idSchedule: scheduleId,
            idCenter: 0,
            platformType: 0,
            bookedSeat,
            idBooking: 0,
            connectionClientId: '',
            checkPaymentRestriction,
            idMemberFee,
            forceBuyWithCredits
        };

        const url = `bookings/schedules/${scheduleId}/members/${selectedMemberId}`;

        return this.post(url, { app: 'booking', version: 2.1 }, bookingActivity, this.showSuccessMessage());
    }

    validateQrCode(scheduleId: number, qrParsed: MemberInfoQr): Observable<any> {
        const url = `bookings/schedules/${scheduleId}/access/validate/qr`;

        return this.post(url, { app: 'booking', version: 2 }, qrParsed);
    }

    list(idSchedule: number): Observable<any> {
        return this.get(`bookings/schedules/${idSchedule}/places`, { app: 'booking', version: 2 });
    }

    private canValidateFull(): boolean {
        return this.permissions.canValidateSchedule
            && !this.permissions.canValidateOnlyMyActivities
            && !this.permissions.canValidateOnlyTodayActivities;
    }

    private canValidateOnlyMyActivities(activity: any): boolean {
        return this.permissions.canValidateSchedule
            && this.permissions.canValidateOnlyMyActivities
            && (this.userService.getIdStaff() === activity?.staff?.id || activity.isScheduleOwner);
    }

    private canValidateOnlyTodayActivities(dateProgram: Date): boolean {
        return this.permissions.canValidateSchedule
            && this.permissions.canValidateOnlyTodayActivities
            && dateProgram
            && dateProgram.setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0);
    }
}
